import type { CompanyCategory, CompanyRow } from './types';
import type { CompanyWithGeo } from './api';

export const CATEGORY_LABEL: Record<CompanyCategory, string> = {
  it_tech: 'IT / Tech',
  finance: 'Finance',
  coworking: 'Coworking',
  hospital: 'Hospital',
  college: 'College',
  government: 'Government',
  manufacturing: 'Manufacturing',
  media: 'Media',
  retail: 'Retail',
  hospitality: 'Hospitality',
  other: 'Other',
};

/** Brand-aligned score bands. navy #252D63 / teal #51BFAA / magenta #B15599. */
export function scoreColor(score: number | null | undefined): string {
  if (score == null) return '#94a3b8'; // slate-400
  if (score >= 75) return '#51BFAA'; // teal — hot
  if (score >= 55) return '#7EA88F';
  if (score >= 35) return '#E0A96D'; // warm amber
  return '#B15599'; // magenta — cold/low
}

export function scoreBand(score: number | null | undefined): string {
  if (score == null) return 'unscored';
  if (score >= 75) return 'hot';
  if (score >= 55) return 'warm';
  if (score >= 35) return 'cool';
  return 'cold';
}

export function formatDistance(m: number | null | undefined): string {
  if (m == null) return '—';
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
}

/** Pick a company's map coordinates: primary location, else first geocoded. */
function pickCoords(c: CompanyWithGeo): { lat: number | null; lng: number | null } {
  const locs = c.company_locations ?? [];
  const primary = locs.find((l) => l.id === c.primary_location_id && l.lat != null);
  const any = primary ?? locs.find((l) => l.lat != null);
  return { lat: any?.lat ?? null, lng: any?.lng ?? null };
}

/**
 * Flatten a company to a table/map row. If `outletId` is given, use that
 * outlet's distance (and drop companies not linked to it); otherwise use the
 * globally-nearest outlet.
 */
export function toRow(c: CompanyWithGeo, outletId?: string): CompanyRow | null {
  const dists = c.company_outlet_distance ?? [];
  let nearest_outlet_id: string | null = null;
  let nearest_distance_m: number | null = null;

  if (outletId) {
    const d = dists.find((x) => x.outlet_id === outletId);
    if (!d) return null;
    nearest_outlet_id = d.outlet_id;
    nearest_distance_m = Number(d.distance_m);
  } else {
    const d = dists.find((x) => x.is_nearest) ??
      [...dists].sort((a, b) => Number(a.distance_m) - Number(b.distance_m))[0];
    nearest_outlet_id = d?.outlet_id ?? null;
    nearest_distance_m = d ? Number(d.distance_m) : null;
  }

  const coords = pickCoords(c);
  const orders_last_90d = (c.company_locations ?? []).reduce((s, l) => s + (l.orders_last_90d ?? 0), 0);

  return {
    ...c,
    nearest_outlet_id,
    nearest_distance_m,
    lat: coords.lat,
    lng: coords.lng,
    orders_last_90d,
  };
}
